<script setup lang="ts">
	import { computed } from "vue";
	import { NIcon } from "naive-ui";
	import { useI18n } from "vue-i18n";
	import { CheckCircleIcon, ErrorCircleIcon, WarningIcon, InfoIcon } from "@/components/icons";

	const { t } = useI18n();

	const notificationTitles = computed(() => ({
		error: t("components.parts.notificationItem.titles.error"),
		warning: t("components.parts.notificationItem.titles.warning"),
		success: t("components.parts.notificationItem.titles.success"),
		info: t("components.parts.notificationItem.titles.info"),
	}));
	const notificationColorVars = {
		error: "red",
		warning: "orange",
		success: "green",
		info: "blue",
	};
	const notificationIcons = {
		error: ErrorCircleIcon,
		warning: WarningIcon,
		success: CheckCircleIcon,
		info: InfoIcon,
	};
	withDefaults(
		defineProps<{
			type: "error" | "warning" | "success" | "info";
			title?: string;
			content: string;
		}>(),
		{
			type: "info",
		}
	);

	const emit = defineEmits<{
		(e: "add"): void;
	}>();
</script>

<template>
	<div class="notification-item">
		<div
			class="notification-item__icon"
			:style="{
				backgroundColor: `var(--bg-${notificationColorVars[type]})`,
				color: `var(--${notificationColorVars[type]})`,
			}">
			<n-icon :size="24"><component :is="notificationIcons[type]" /></n-icon>
		</div>
		<div class="notification-item__body">
			<div class="notification-item__title">
				{{ title || notificationTitles[type] }}
			</div>
			<div class="notification-item__content">{{ content }}</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
	.notification-item {
		@include flex(1.6rem, row, start, start);

		width: 100%;

		&__icon {
			@include flex(0, row, center, center);

			width: 4.8rem;
			height: 4.8rem;
			border-radius: 50%;
			flex-shrink: 0;
		}

		&__body {
			@include flex(0, column, start, start);
		}

		&__title {
			@include getFont("h5");
			color: var(--gs-100);
		}

		&__content {
			@include getFont("p3");
			color: var(--gs-200);
		}
	}
</style>
@/components/icons
